import { useEffect, useMemo, useRef, useState } from 'react';

import { ALERT_HOVER_PREVIEW_CELL_ATTR } from '../../hooks/useToaster/constants.ts';
import { useAlerts } from '../referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { useAppDispatch } from '../../redux/hooks.ts';
import { getActiveAlertForRequiredField } from './getActiveAlertForRequiredField.ts';
import {
  shouldPreserveAlertHoverPreview,
  syncAlertHoverPreview,
} from './syncAlertHoverPreview.ts';
import { useRequiredFieldAlertHighlight } from './useRequiredFieldAlertHighlight.ts';

export const useRequiredFieldAlertInteraction = (fieldName: string) => {
  const dispatch = useAppDispatch();
  const alerts = useAlerts();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const { isAlertTarget, isAlertHighlighted, clearAlertTarget } =
    useRequiredFieldAlertHighlight(fieldName);

  const targetingAlert = useMemo(
    () => getActiveAlertForRequiredField(alerts, fieldName),
    [alerts, fieldName],
  );

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return undefined;
    }

    const handleFocusIn = () => {
      setIsFocused(true);
      if (isAlertTarget) {
        clearAlertTarget();
      }
    };

    const handleFocusOut = (event: FocusEvent) => {
      if (element.contains(event.relatedTarget as Node)) {
        return;
      }
      setIsFocused(false);
    };

    element.addEventListener('focusin', handleFocusIn);
    element.addEventListener('focusout', handleFocusOut);
    return () => {
      element.removeEventListener('focusin', handleFocusIn);
      element.removeEventListener('focusout', handleFocusOut);
    };
  }, [clearAlertTarget, isAlertTarget]);

  useEffect(() => {
    if (!targetingAlert) {
      return undefined;
    }

    const element = containerRef.current;
    if (!element) {
      return undefined;
    }

    element.setAttribute(ALERT_HOVER_PREVIEW_CELL_ATTR, '');

    const handleMouseEnter = () => {
      syncAlertHoverPreview(dispatch, targetingAlert, { scrollTargetIntoView: false });
    };

    const handleMouseLeave = (event: MouseEvent) => {
      if (element.contains(event.relatedTarget as Node)) {
        return;
      }
      if (shouldPreserveAlertHoverPreview(event.relatedTarget as HTMLElement | null)) {
        return;
      }
      syncAlertHoverPreview(dispatch, null);
    };

    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      element.removeAttribute(ALERT_HOVER_PREVIEW_CELL_ATTR);
      element.removeEventListener('mouseenter', handleMouseEnter);
      element.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [dispatch, targetingAlert]);

  return {
    containerRef,
    isAlertTarget,
    isAlertHighlighted,
    isFocused,
    clearAlertTarget,
  };
};
